import { getInstanceByDom } from "echarts";
import mapValues from "lodash/mapValues";
import { useCallback } from "react";

export const reverseLegendSelected = ({ selected, name }) => {
  const currentValue = selected[name];
  const inverseSelected = { ...selected, [name]: !currentValue };
  const allSelected = !Object.values(inverseSelected).includes(false);

  // If some legends are not selected, just return current selected state
  if (!allSelected) return selected;

  // Otherwise select only the clicked legend
  const values = mapValues(selected, () => false);
  return { ...values, [name]: true };
};

const findSelectedValueInOptions = (options, dataIndex) => {
  const selectedValue = options?.xAxis?.[0]?.data?.[dataIndex];
  return selectedValue?.value ?? selectedValue;
};

export const getSelectedValuesFromSelectChanged = (params, options) => {
  const selected = params?.selected?.[0]?.dataIndex ?? [];
  return selected
    .map((value) => findSelectedValueInOptions(options, value))
    .filter((value) => value !== undefined);
};

export const useChartBaseUtils = (chartRef) => {
  const getChart = useCallback(() => {
    if (!chartRef?.current) return undefined;
    return getInstanceByDom(chartRef.current);
  }, [chartRef.current]);

  const getOptions = useCallback(() => {
    const chart = getChart();
    return chart?.getOption() ?? {};
  }, [getChart]);

  const resizeChart = useCallback(() => {
    getChart()?.resize();
  }, [getChart]);

  const setOptions = useCallback(
    (options) => {
      const chart = getChart();
      if (!chart) return;

      chart.setOption(options);
      chart.resize();
    },
    [getChart]
  );

  const setLoading = useCallback(
    (loading) => {
      const chart = getChart();
      if (!chart) return;

      loading === true ? chart.showLoading() : chart.hideLoading();
    },
    [getChart]
  );

  const dispatchAction = useCallback(
    (payload) => {
      const chart = getChart();
      if (!chart) return;

      chart.dispatchAction(payload);
    },
    [getChart]
  );

  return {
    getChart,
    getOptions,
    resizeChart,
    setOptions,
    setLoading,
    dispatchAction,
  };
};
